import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  useTheme,
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import { useAuth } from '../hooks/useAuth';
import { authService } from '../services/authService';

/**
 * Standalone MFA setup page shown when an admin has enforced MFA for a user.
 * No sidebar / navigation -- user MUST complete setup before using the app.
 */
const MfaSetupPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.primary.main;
  const { user, refreshUser, logout } = useAuth();

  const [loading, setLoading] = useState(false);
  const [qrCodeBase64, setQrCodeBase64] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'intro' | 'scan'>('intro');

  const handleStartSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await authService.setup2FA();
      setQrCodeBase64(data.qr_code_base64);
      setTotpSecret(data.secret);
      setStep('scan');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Einrichten der 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      setError('Bitte einen 6-stelligen Code eingeben');
      return;
    }
    setVerifyLoading(true);
    setError('');
    try {
      await authService.verify2FA(totpSecret, verifyCode);
      // Refresh user data -- has_2fa will now be true, redirect happens automatically
      await refreshUser();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ungültiger Code. Bitte erneut versuchen.');
    } finally {
      setVerifyLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: isDark ? '#181c34' : '#f0f2f5',
        background: isDark
          ? 'radial-gradient(ellipse at 50% 0%, #1e2340 0%, #181c34 70%)'
          : 'radial-gradient(ellipse at 50% 0%, #f5f7fa 0%, #e8ecf0 70%)',
      }}
    >
      {/* Subtle grid pattern overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage:
            'linear-gradient(#2e356008 1px, transparent 1px), linear-gradient(90deg, #2e356008 1px, transparent 1px)',
          backgroundSize: '60px 60px',
          pointerEvents: 'none',
        }}
      />

      <Card
        sx={{
          width: { xs: '100%', sm: 520 },
          maxWidth: '100%',
          mx: { xs: 2, sm: 'auto' },
          bgcolor: isDark ? '#212744' : '#ffffff',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          position: 'relative',
          overflow: 'visible',
          boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.5)' : '0 20px 60px rgba(0,0,0,0.1)',
        }}
      >
        {/* Top accent line */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '10%',
            right: '10%',
            height: '2px',
            background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
            borderRadius: '2px',
          }}
        />

        <CardContent sx={{ p: { xs: 3, sm: 4 }, pt: { xs: 4, sm: 5 } }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <ShieldIcon sx={{ fontSize: 48, color: accent, mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: accent }}>
              2FA-Einrichtung erforderlich
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              {user?.vorname}, Ihr Administrator hat die Zwei-Faktor-Authentifizierung
              für Ihr Konto aktiviert. Bitte richten Sie diese jetzt ein, um fortzufahren.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {step === 'intro' && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Sie benötigen eine Authenticator-App auf Ihrem Smartphone
                (z.B. Google Authenticator, Authy oder Microsoft Authenticator).
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleStartSetup}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <ShieldIcon />}
                sx={{ px: 4, py: 1.5 }}
              >
                Jetzt einrichten
              </Button>
            </Box>
          )}

          {step === 'scan' && (
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, textAlign: 'center' }}>
                Scannen Sie den QR-Code mit Ihrer Authenticator-App
                und geben Sie den generierten 6-stelligen Code ein.
              </Typography>

              {qrCodeBase64 && (
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Box
                    component="img"
                    src={`data:image/png;base64,${qrCodeBase64}`}
                    alt="2FA QR-Code"
                    sx={{
                      width: 200,
                      height: 200,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      p: 1,
                      bgcolor: '#fff',
                    }}
                  />
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Backup-Schlüssel (sicher aufbewahren):
              </Typography>
              <Box sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: isDark ? '#1a1f3c' : '#f5f7fa',
                border: `1px solid ${theme.palette.divider}`,
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                letterSpacing: '0.1em',
                textAlign: 'center',
                mb: 3,
                wordBreak: 'break-all',
                userSelect: 'all',
              }}>
                {totpSecret}
              </Box>

              <TextField
                label="6-stelliger Code"
                fullWidth
                value={verifyCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerifyCode(val);
                }}
                placeholder="000000"
                inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                autoFocus
                sx={{ mb: 2 }}
              />

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleVerify}
                disabled={verifyLoading || verifyCode.length !== 6}
                sx={{ py: 1.5 }}
              >
                {verifyLoading ? <CircularProgress size={24} /> : 'Verifizieren & Aktivieren'}
              </Button>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Button
              size="small"
              onClick={logout}
              sx={{ color: 'text.secondary', fontSize: '0.8rem' }}
            >
              Abmelden
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MfaSetupPage;
